import { useCategoryPage } from '../CategoryPage/useCategoryPage'
import { useCasinoDeps } from '../../useDeps'
import type { Category, Game } from '../../types'

interface CasinoFilters {
  categoryTheme: number | 'all' | undefined
  providerCode: string | undefined
  isFavorite: boolean
}

interface CategoryWithGames extends Category {
  gamesTotal: number
  games: Game[]
}

export function useCasinoOverview() {
  const PER_PAGE_COUNT = 18
  const {
    otherCategories,
    isLoading: isLoadingCasinoStore,
    isReady: isReadyCasinoStore,
  } = storeToRefs(useCasinoStore())
  const { getCategoryByCode } = useCasinoStore()
  const { categoryTheme, categoryCode, isFavorite, providerCode } =
    useCategoryPage()

  const filters = useSessionStorage<CasinoFilters>('casino-filters', {
    categoryTheme: undefined,
    providerCode: undefined,
    isFavorite: false,
  })

  const categoryTitle = computed(
    () =>
      getCategoryByCode(categoryCode.value)?.labelName ??
      categoryCode.value?.toString(),
  )

  const otherCategoriesOutput = computed(() => {
    const childCategories = otherCategories.value.filter(
      (category) =>
        category.parentCode === categoryCode.value && category.type === 'other',
    )

    return childCategories
  })

  const { casinoTournament: tournament } = useCasinoDeps()

  const { isMobile } = usePlatform()
  const categoriesData = ref<Record<string, CategoryWithGames>>({})
  const isGamesLoading = ref(false)
  const stFetch = useRawStFetch()

  const requestGamesUrl = computed(() =>
    filters.value.isFavorite
      ? '/casino/game/favorite/find'
      : '/casino/game/find',
  )

  const findParamCategoryId = computed<number[] | undefined>(() => {
    if (!filters.value.categoryTheme) return undefined
    return filters.value.categoryTheme === 'all'
      ? undefined
      : [filters.value.categoryTheme]
  })

  async function fetchGames(category: Category) {
    isGamesLoading.value = true
    const { data, error } = await stFetch(requestGamesUrl.value, {
      method: 'post',
      body: {
        params: {
          categoryCode: category.code,
          ...(isMobile.value ? { forMobile: true } : { forDesktop: true }),
          providerCode:
            filters.value.providerCode === 'all'
              ? undefined
              : filters.value.providerCode,
          categoryId: findParamCategoryId.value,
        },
        pagination: {
          page: 0,
          perPage: PER_PAGE_COUNT,
          orderBy: [
            {
              sortOrder: 'ASC' as const,
              fieldName: 'forbiddenByCountry' as const,
            },
            {
              sortOrder: 'DESC' as const,
              fieldName: 'weightInCategory' as const,
            },
          ],
        },
      },
    })
    if (error) {
      console.error(error)
    } else {
      categoriesData.value[category.code] = {
        ...category,
        gamesTotal: data.paging.total,
        games: data?.data ?? [],
      }
    }
    isGamesLoading.value = false
  }

  async function fetchAllGames() {
    await until(isReadyCasinoStore).toBe(true)
    const categoryByCode = getCategoryByCode(categoryCode.value)
    if (!categoryByCode) return
    fetchGames(categoryByCode)
    otherCategoriesOutput.value.forEach((category) => {
      fetchGames(category)
    })
  }

  const otherCategoriesData = computed(() =>
    Object.values(categoriesData.value)
      .filter((category) => category.code !== categoryCode.value)
      .sort((c1, c2) => c2.weight - c1.weight),
  )

  const mainCategoryData = computed(
    () => categoriesData.value[categoryCode.value],
  )
  const hasGames = computed(() =>
    Object.values(categoriesData.value).some(
      (category) => category.gamesTotal > 0,
    ),
  )
  const isLoading = computed(
    () => isGamesLoading.value || isLoadingCasinoStore.value,
  )

  watch(
    () => filters.value,
    () => {
      fetchAllGames()
    },
    { immediate: true },
  )

  return {
    categoryCode,
    categoryTheme,
    providerCode,
    otherCategoriesOutput,
    isLoading,
    categoryTitle,
    isFavorite,
    hasGames,
    tournament,
    categoriesData,
    mainCategoryData,
    otherCategoriesData,
  }
}
